import { sortBy } from 'lodash-es'

import {
  ALGEBRA2_LEARNING_LABS_PROGRAM_SLUG,
  COLLEGE_ADMISSIONS_WORKSHOPS_PROGRAM_SLUG,
  PRE_CALCULUS_LEARNING_LABS_PROGRAM_SLUG,
} from '@/client/services/programs/constants'
import {
  ALGEBRA2_LEARNING_LABS_WORLD_SLUG,
  COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG,
  PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG,
  SAT_BOOTCAMP_WORLD_SLUG,
} from '@/client/services/worlds/constants'

import useSATBootcampTodos from '@/components/SATBootcamp/hooks/todos/useSATBootcampTodos'

import type { WorldDetailsForTodoList } from '../TodoList'
import type { TodoItem } from '../types'
import { useProgramTodos } from './programs/useProgramTodos'
import { useAccountTodos } from './useAccountTodos'
import { useNextStepTodos } from './useNextStepTodos'
import { usePeerReviewTodos } from './usePeerReviewTodos'
import { usePracticeTodos } from './usePracticeTodos'
import useWorldSlugToWorld from './useWorldSlugToWorld'

// Overall Prioritization
// - Anything required to complete your account (add phone number)
// - Any bootcamp to-dos
// - Anything with a deadline
// - If you are a moderator, applications to review in your subworlds (ordered)
// - If you are a coach, peer reviews in your subworlds (ordered)
// - If you are a tutor, then help channels (ordered)
// - If you are not a tutor in any subworld yet, then become-a-tutor steps (ordered)
// - If you are a learner in a certification subworld, then getting certified

function sortAndFilterTodos({
  todos,
  excludeGeneralTodos,
  worlds,
}: {
  todos: TodoItem[]
  excludeGeneralTodos: boolean
  worlds: WorldDetailsForTodoList[]
}) {
  const filteredTodos = todos.filter(todo => !excludeGeneralTodos || todo.worldSlug != null)

  // TODO: Be more intelligent about this sorting
  const sortedTodos = sortBy(filteredTodos, [
    todo => (todo.deadline == null ? Number.POSITIVE_INFINITY : todo.deadline.toMillis()),
    todo => todo.priority,
    todo => (todo.disabled ? 1 : 0),
  ])

  return sortedTodos.map(todo => ({
    ...todo,
    world:
      todo.worldSlug == null ? null : (worlds.find(world => world.slug === todo.worldSlug) ?? null),
  }))
}

function useAllProgramTodos({ worldSlugs }: { worldSlugs: string[] }) {
  const satBootcampTodos = useSATBootcampTodos()
  const cawTodos = useProgramTodos(COLLEGE_ADMISSIONS_WORKSHOPS_PROGRAM_SLUG)
  const algebra2LearningLabsTodos = useProgramTodos(ALGEBRA2_LEARNING_LABS_PROGRAM_SLUG)
  const precalcLearningLabsTodos = useProgramTodos(PRE_CALCULUS_LEARNING_LABS_PROGRAM_SLUG)

  const todos = [
    ...(worldSlugs.includes(SAT_BOOTCAMP_WORLD_SLUG) ? satBootcampTodos : []),
    ...(worldSlugs.includes(COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG) ? cawTodos : []),
    ...(worldSlugs.includes(ALGEBRA2_LEARNING_LABS_WORLD_SLUG) ? algebra2LearningLabsTodos : []),
    ...(worldSlugs.includes(PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG) ? precalcLearningLabsTodos : []),
  ]
  return todos
}

export function useSeriesPageTodos({
  world,
}: {
  // This function doesn't assume that world is known during SSR
  world: WorldDetailsForTodoList
}) {
  const todos = useAllProgramTodos({ worldSlugs: [world.slug] })
  const onlySeriesPageTodos = todos.filter(todo => todo.showOnSeriesPage)

  return sortAndFilterTodos({
    todos: onlySeriesPageTodos,
    excludeGeneralTodos: true,
    worlds: [world],
  })
}

export function useAllTodos({
  worldSlugs,
  excludeGeneralTodos = false,
}: {
  // This function assumes worldSlugs are known during SSR (instead of through a query)
  worldSlugs: string[]
  excludeGeneralTodos?: boolean
}) {
  const accountTodos = useAccountTodos()
  const peerReviewTodos = usePeerReviewTodos({ worldSlugs })
  const nextStepTodos = useNextStepTodos({ worldSlugs })
  const programWorldTodos = useAllProgramTodos({ worldSlugs })
  const practiceTodos = usePracticeTodos()

  const todos = [
    ...accountTodos,
    ...programWorldTodos,
    ...peerReviewTodos,
    ...nextStepTodos,
    ...practiceTodos,
  ]
  const worlds = useWorldSlugToWorld({ worldSlugs })

  return sortAndFilterTodos({ todos, excludeGeneralTodos, worlds })
}
